import { Check } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * One choice in a Preferences radio group: an optional preview of what the
 * choice looks like, its name, and a line on what it is. The selected tile
 * carries the primary ring and a filled check; the rest a hollow one, so the
 * group reads as "pick one" at a glance.
 *
 * Rendered as `role="radio"` inside the caller's `role="radiogroup"`.
 */
export function OptionTile({
  label,
  description,
  preview,
  selected,
  disabled = false,
  onSelect,
}: {
  label: ReactNode;
  description?: ReactNode;
  preview?: ReactNode;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "group flex flex-col gap-3 rounded-lg border bg-card p-2.5 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-primary ring-2 ring-primary-dim"
          : "border-border enabled:hover:border-border-strong enabled:hover:shadow-card",
      )}
    >
      {preview ? (
        <div className="overflow-hidden rounded-md border border-border">{preview}</div>
      ) : null}
      <div className="flex items-start justify-between gap-2 px-0.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {description ? (
            <span className="text-xs text-muted-foreground">{description}</span>
          ) : null}
        </div>
        <span
          aria-hidden
          className={cn(
            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
            selected
              ? "border-primary-fill bg-primary-fill text-primary-foreground"
              : "border-border-strong",
          )}
        >
          {selected ? <Check size={10} strokeWidth={3} /> : null}
        </span>
      </div>
    </button>
  );
}
